import { useState, useEffect } from 'react';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { BarChart3, LineChart, PieChart, Table, Plus, Sparkles } from 'lucide-react';
import { DynamicChart } from './DynamicChart';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';

interface DataSource {
  id: string;
  name: string;
  type: string;
  columns: { name: string; type: string }[];
  data: any[];
}

interface ChartConfig {
  id: string;
  type: 'bar' | 'line' | 'pie' | 'table';
  title: string;
  dataSource: string;
  xAxis?: string;
  yAxis?: string;
  category?: string;
  data: any[];
}

export const DashboardGenerator = () => {
  const [dataSources, setDataSources] = useState<DataSource[]>([]);
  const [charts, setCharts] = useState<ChartConfig[]>([]);
  const [isOpen, setIsOpen] = useState(false);
  const [loading, setLoading] = useState(false);

  // Form state
  const [chartType, setChartType] = useState<'bar' | 'line' | 'pie' | 'table'>('bar');
  const [chartTitle, setChartTitle] = useState('');
  const [selectedDataSource, setSelectedDataSource] = useState<string>('');
  const [selectedXAxis, setSelectedXAxis] = useState<string>('');
  const [selectedYAxis, setSelectedYAxis] = useState<string>('');
  const [selectedCategory, setSelectedCategory] = useState<string>('');

  useEffect(() => {
    loadDataSources();
  }, []);

  const loadDataSources = async () => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;

      // Load uploaded files
      const { data: files, error } = await supabase
        .from('file_storage')
        .select('*')
        .eq('user_id', user.id)
        .eq('is_folder', false)
        .or('mime_type.like.%csv%,mime_type.like.%json%,mime_type.like.%excel%');

      if (error) throw error;

      const sources: DataSource[] = [];
      
      for (const file of files || []) {
        try {
          const { data: fileData, error: downloadError } = await supabase.storage
            .from('user_files')
            .download(file.file_path);
          
          if (downloadError) continue;
          
          const text = await fileData.text();
          let parsedData: any[] = [];
          let columns: { name: string; type: string }[] = [];

          if (file.mime_type?.includes('csv') || file.file_name.endsWith('.csv')) {
            const lines = text.split('\n').filter(line => line.trim());
            if (lines.length > 1) {
              const headers = lines[0].split(',').map(h => h.trim().replace(/"/g, ''));
              parsedData = lines.slice(1, 101).map(line => { // Limit to 100 rows for performance
                const values = line.split(',').map(v => v.trim().replace(/"/g, ''));
                const obj: any = {};
                headers.forEach((header, index) => {
                  obj[header] = values[index] || '';
                });
                return obj;
              });

              columns = headers.map(header => {
                const values = parsedData.map(row => row[header]).filter(v => v != null && v !== '');
                const isNumeric = values.every(v => !isNaN(Number(v)) && isFinite(Number(v)));
                return { name: header, type: isNumeric ? 'number' : 'string' };
              });
            }
          }

          if (parsedData.length > 0) {
            sources.push({
              id: file.id,
              name: file.file_name,
              type: 'file',
              columns,
              data: parsedData
            });
          }
        } catch (err) {
          console.error(`Error processing file ${file.file_name}:`, err);
        }
      }

      setDataSources(sources);
    } catch (error) {
      console.error('Error loading data sources:', error);
      toast.error('Failed to load data sources');
    }
  };

  const generateChart = () => {
    if (!selectedDataSource || !chartTitle) {
      toast.error('Please select a data source and enter a chart title');
      return;
    }

    const dataSource = dataSources.find(ds => ds.id === selectedDataSource);
    if (!dataSource) return;

    let chartData = dataSource.data;
    let processedData: any[] = [];

    switch (chartType) {
      case 'bar':
      case 'line':
        if (!selectedXAxis || !selectedYAxis) {
          toast.error('Please select both X and Y axis columns');
          return;
        }
        processedData = chartData.map(row => ({
          name: row[selectedXAxis],
          value: Number(row[selectedYAxis]) || 0
        }));
        break;

      case 'pie':
        if (!selectedCategory) {
          toast.error('Please select a category column for pie chart');
          return;
        }
        const categoryCount = chartData.reduce((acc, row) => {
          const category = row[selectedCategory];
          acc[category] = (acc[category] || 0) + 1;
          return acc;
        }, {} as Record<string, number>);
        
        processedData = Object.entries(categoryCount).map(([name, value]) => ({
          name,
          value
        }));
        break;

      case 'table':
        processedData = chartData.slice(0, 10); // Show first 10 rows
        break;
    }

    const newChart: ChartConfig = {
      id: `chart-${Date.now()}`,
      type: chartType,
      title: chartTitle,
      dataSource: selectedDataSource,
      xAxis: selectedXAxis,
      yAxis: selectedYAxis,
      category: selectedCategory,
      data: processedData
    };

    setCharts(prev => [...prev, newChart]);
    setIsOpen(false);
    resetForm();
    toast.success('Chart generated successfully!');
  };

  const resetForm = () => {
    setChartTitle('');
    setSelectedDataSource('');
    setSelectedXAxis('');
    setSelectedYAxis('');
    setSelectedCategory('');
    setChartType('bar');
  };

  const removeChart = (chartId: string) => {
    setCharts(prev => prev.filter(chart => chart.id !== chartId));
    toast.success('Chart removed');
  };

  const getChartIcon = (type: string) => {
    switch (type) {
      case 'bar': return BarChart3;
      case 'line': return LineChart;
      case 'pie': return PieChart;
      case 'table': return Table;
      default: return BarChart3;
    }
  };

  const selectedSource = dataSources.find(ds => ds.id === selectedDataSource);
  const numericColumns = selectedSource?.columns.filter(col => col.type === 'number') || [];
  const stringColumns = selectedSource?.columns.filter(col => col.type === 'string') || [];

  return (
    <div className="space-y-6">
      <Card className="p-6">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h3 className="text-lg font-semibold flex items-center gap-2">
              <Sparkles className="h-5 w-5 text-primary" />
              Dashboard Generator
            </h3>
            <p className="text-sm text-muted-foreground">
              Create professional charts from your data like Google Data Studio
            </p>
          </div>
          <Dialog open={isOpen} onOpenChange={setIsOpen}>
            <DialogTrigger asChild>
              <Button>
                <Plus className="mr-2 h-4 w-4" />
                Create Chart
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-2xl">
              <DialogHeader>
                <DialogTitle>Create New Chart</DialogTitle>
              </DialogHeader>
              
              <div className="space-y-6">
                {/* Chart Type Selection */}
                <div>
                  <Label className="text-sm font-medium">Chart Type</Label>
                  <div className="grid grid-cols-4 gap-2 mt-2">
                    {[
                      { type: 'bar', icon: BarChart3, label: 'Bar Chart' },
                      { type: 'line', icon: LineChart, label: 'Line Chart' },
                      { type: 'pie', icon: PieChart, label: 'Pie Chart' },
                      { type: 'table', icon: Table, label: 'Data Table' }
                    ].map(({ type, icon: Icon, label }) => (
                      <Button
                        key={type}
                        variant={chartType === type ? 'default' : 'outline'}
                        className="h-20 flex flex-col items-center justify-center"
                        onClick={() => setChartType(type as any)}
                      >
                        <Icon className="h-6 w-6 mb-1" />
                        <span className="text-xs">{label}</span>
                      </Button>
                    ))}
                  </div>
                </div>

                {/* Chart Title */}
                <div>
                  <Label htmlFor="title" className="text-sm font-medium">Chart Title</Label>
                  <Input
                    id="title"
                    placeholder="Enter chart title"
                    value={chartTitle}
                    onChange={(e) => setChartTitle(e.target.value)}
                  />
                </div>

                {/* Data Source Selection */}
                <div>
                  <Label className="text-sm font-medium">Data Source</Label>
                  <Select value={selectedDataSource} onValueChange={setSelectedDataSource}>
                    <SelectTrigger>
                      <SelectValue placeholder="Select data source" />
                    </SelectTrigger>
                    <SelectContent>
                      {dataSources.map(source => (
                        <SelectItem key={source.id} value={source.id}>
                          <div className="flex items-center gap-2">
                            <Badge variant="secondary" className="text-xs">
                              {source.data.length} rows
                            </Badge>
                            {source.name}
                          </div>
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                {/* Column Selection based on chart type */}
                {selectedSource && (
                  <div className="space-y-4">
                    {(chartType === 'bar' || chartType === 'line') && (
                      <>
                        <div>
                          <Label className="text-sm font-medium">X-Axis (Categories)</Label>
                          <Select value={selectedXAxis} onValueChange={setSelectedXAxis}>
                            <SelectTrigger>
                              <SelectValue placeholder="Select X-axis column" />
                            </SelectTrigger>
                            <SelectContent>
                              {selectedSource.columns.map(col => (
                                <SelectItem key={col.name} value={col.name}>
                                  <div className="flex items-center gap-2">
                                    <Badge variant={col.type === 'number' ? 'default' : 'secondary'} className="text-xs">
                                      {col.type}
                                    </Badge>
                                    {col.name}
                                  </div>
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>
                        <div>
                          <Label className="text-sm font-medium">Y-Axis (Values)</Label>
                          <Select value={selectedYAxis} onValueChange={setSelectedYAxis}>
                            <SelectTrigger>
                              <SelectValue placeholder="Select Y-axis column" />
                            </SelectTrigger>
                            <SelectContent>
                              {numericColumns.map(col => (
                                <SelectItem key={col.name} value={col.name}>
                                  <div className="flex items-center gap-2">
                                    <Badge variant="default" className="text-xs">number</Badge>
                                    {col.name}
                                  </div>
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>
                      </>
                    )}

                    {chartType === 'pie' && (
                      <div>
                        <Label className="text-sm font-medium">Category Column</Label>
                        <Select value={selectedCategory} onValueChange={setSelectedCategory}>
                          <SelectTrigger>
                            <SelectValue placeholder="Select category column" />
                          </SelectTrigger>
                          <SelectContent>
                            {stringColumns.map(col => (
                              <SelectItem key={col.name} value={col.name}>
                                <div className="flex items-center gap-2">
                                  <Badge variant="secondary" className="text-xs">string</Badge>
                                  {col.name}
                                </div>
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                    )}
                  </div>
                )}

                <div className="flex justify-end gap-2">
                  <Button variant="outline" onClick={() => setIsOpen(false)}>Cancel</Button>
                  <Button onClick={generateChart} disabled={loading}>
                    Generate Chart
                  </Button>
                </div>
              </div>
            </DialogContent>
          </Dialog>
        </div>

        {dataSources.length === 0 && (
          <div className="text-center py-8">
            <p className="text-muted-foreground">No data sources available.</p>
            <p className="text-sm text-muted-foreground">Upload CSV files to get started.</p>
          </div>
        )}

        {dataSources.length > 0 && (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {dataSources.map(source => (
              <Card key={source.id} className="p-4">
                <div className="flex items-center gap-2 mb-2">
                  <Badge variant="outline">{source.columns.length} columns</Badge>
                  <Badge variant="secondary">{source.data.length} rows</Badge>
                </div>
                <h4 className="font-medium text-sm">{source.name}</h4>
                <p className="text-xs text-muted-foreground mt-1">
                  Columns: {source.columns.slice(0, 3).map(col => col.name).join(', ')}
                  {source.columns.length > 3 && '...'}
                </p>
              </Card>
            ))}
          </div>
        )}
      </Card>

      {/* Generated Charts */}
      {charts.length > 0 && (
        <div className="space-y-4">
          <h3 className="text-lg font-semibold">Generated Charts</h3>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {charts.map(chart => (
              <div key={chart.id} className="relative">
                <Button
                  variant="ghost"
                  size="sm"
                  className="absolute top-2 right-2 z-10"
                  onClick={() => removeChart(chart.id)}
                >
                  ×
                </Button>
                {chart.type === 'table' ? (
                  <Card className="p-4">
                    <h4 className="font-medium mb-4">{chart.title}</h4>
                    <div className="overflow-x-auto">
                      <table className="w-full text-sm">
                        <thead>
                          <tr className="border-b">
                            {Object.keys(chart.data[0] || {}).map(key => (
                              <th key={key} className="text-left p-2">{key}</th>
                            ))}
                          </tr>
                        </thead>
                        <tbody>
                          {chart.data.map((row, idx) => (
                            <tr key={idx} className="border-b">
                              {Object.values(row).map((value, cidx) => (
                                <td key={cidx} className="p-2">{String(value)}</td>
                              ))}
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </Card>
                ) : (
                  <DynamicChart
                    config={{
                      id: chart.id,
                      type: chart.type,
                      title: chart.title,
                      data: chart.data,
                      xAxis: chart.xAxis,
                      yAxis: chart.yAxis
                    }}
                    data={chart.data}
                  />
                )}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};